import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Box, Typography } from '@material-ui/core';
import { ToastContainer, toast } from 'react-toastify';

import useIsCompany from '../../../myHooks//useIsCompany';
import { socialMediaAlgorithmCalculation } from '../../../redux/actions/Firebase';
import { fetchOwnedAgencyDataBySurveyId } from '../../../redux/actions/Data';
import { fetchSpecificSurveyData } from '../../../redux/actions/Data';
import { AISocialMediaStrategyGenerator } from '../../../API/agent';
import { getSocialMediaPlatform, getPostMediaType } from '../../../helperFunctions/getSurveysWithExtractedFields';
import { makeURL, findLatestExistingBatchData, batchDataSort } from '../../../helperFunctions';
import { DEFAULT_BATCH_COUNTRY } from '../../../config/batchConfig';

import PageContainer from '../../../myComponents/pageContainer';
import PageLoader from '../../../myComponents/loaders/pageLoader';
import ChartCalculations from './chartCalculations';
import HeaderSelector from '../../../myComponents/headerSelector';
import Filters from '../../../myComponents/filters/Filters';
import PDFExportButton from '../../../myComponents/PDFExportButton';
import UserDropdownHeader from '../../../myComponents/userDropdownHeader';
import BuyerBatchSelector from '../../../myComponents/buyerBatchSelector';

const SocialMediaDashboard = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const isCompany = useIsCompany();
  const appData = useSelector(({ data }) => data);
  const PDFContentRef = useRef(null);

  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [useEffectShouldTrigger, setUseEffectShouldTrigger] = useState(true);

  //Ai strategy state
  const [AIStrategyGenerator, setAIStrategyGenerator] = useState({
    text: '',
    loading: true,
  });

  //Input States
  const [pickedBrandPersona, setPickedBrandPersona] = useState(null);
  const [brandPersonaPickedId, setBrandPersonaPickedId] = useState(null);

  const [pickedSocialPost, setPickedSocialPost] = useState(null);
  const [socialPostPickedId, setSocialPostPickedId] = useState(null);

  const [pickedBatchData, setPickedBatchData] = useState(null);
  const [pickedBatchCountry, setPickedBatchCountry] = useState(DEFAULT_BATCH_COUNTRY);
  const [chosenFilters, setChosenFilters] = useState(null);

  //Calculation state
  const [calculations, setCalculations] = useState({
    loading: false,
    results: null,
  });

  useEffect(() => {
    if (socialPostPickedId) {
      history.push(makeURL(`/marketing-content/${socialPostPickedId}`));
      setUseEffectShouldTrigger(true);
    }
  }, [socialPostPickedId]);

  useEffect(() => {
    if (brandPersonaPickedId) {
      setUseEffectShouldTrigger(true);
    }
  }, [brandPersonaPickedId]);

  useEffect(() => {
    if (!appData.loading && !appData.loadingAgencyData && useEffectShouldTrigger) {
      setUseEffectShouldTrigger(false);
      if (appData.error) {
        showToastWarn();
      } else {
        if (!id) {
          history.push(makeURL('/marketing-content/all'));
        } else {
          const pickedSocialMediaPost = !appData.socialPosts ? false : appData.socialPosts.find(e => e.id === id);
          if (!pickedSocialMediaPost) {
            if (!isCompany) return history.push(makeURL('/marketing-content/all'));
            else {
              dispatch(fetchOwnedAgencyDataBySurveyId(id, 'social'));
              setUseEffectShouldTrigger(true);
            }
          } else {
            if (!pickedBatchData) {
              const { latestQuarter, latestYear } = findLatestExistingBatchData(
                appData.combinedBatchDataCodes[DEFAULT_BATCH_COUNTRY],
              );
              const pickedBatchToSet = {
                quarter: [latestQuarter],
                year: latestYear,
              };

              setPickedBatchData(pickedBatchToSet);
              setPickedBatchCountry(DEFAULT_BATCH_COUNTRY);

              socialPostChangeHandler(pickedSocialMediaPost, pickedBatchToSet, DEFAULT_BATCH_COUNTRY);
            } else {
              socialPostChangeHandler(pickedSocialMediaPost, pickedBatchData, pickedBatchCountry);
            }

            setPageIsLoading(false);
          }
        }
      }
    }
  }, [appData.loading, appData.loadingAgencyData, id, useEffectShouldTrigger]);

  const socialPostChangeHandler = (pickedSocialPost, newPickedBatchData, newPickedBatchCountry) => {
    if (calculations.loading) return;
    let pickedBrandPersona = null;

    if (!brandPersonaPickedId) {
      pickedBrandPersona = appData.brandPersonas.find(persona => persona.id == pickedSocialPost.Post_Brand_Persona);
    } else {
      pickedBrandPersona = appData.brandPersonas.find(persona => persona.id == brandPersonaPickedId);
    }

    setPickedSocialPost(pickedSocialPost);
    setPickedBrandPersona(pickedBrandPersona);
    setBrandPersonaPickedId(pickedBrandPersona.id);
    calculateHandler(pickedSocialPost, pickedBrandPersona, chosenFilters, newPickedBatchData, newPickedBatchCountry);
  };

  const calculateHandler = (socialMediaPost, pickedPersona, filters, newPickedBatchData, newPickedBatchCountry) => {
    setCalculations(prevState => ({ ...prevState, loading: true }));
    socialMediaAlgorithmCalculation(
      {
        filters: filters,
        socialPost: socialMediaPost,
        persona: pickedPersona,
        batches: newPickedBatchData,
        batchCountry: newPickedBatchCountry,
      },
      dispatch,
      appData.visibleAgencies,
    )
      .then(res => {
        setCalculations({ results: res, loading: false });

        //Check if marketing strategy should be generated
        const {
          marketingStrategies,
          Post_Title,
          id,
          Post_Client_Brief,
          Post_Target_Audience,
          Post_Budget,
        } = socialMediaPost;
        if (typeof marketingStrategies !== 'undefined' && typeof marketingStrategies[pickedPersona.id] !== 'undefined') {
          setAIStrategyGenerator({
            loading: false,
            text: marketingStrategies[pickedPersona.id],
          });
        } else {
          //Generate new marketing strategy
          setAIStrategyGenerator({ ...AIStrategyGenerator, loading: true });

          const { Name } = pickedPersona;
          const isSocialMedia = socialMediaPost.Post_Media_Type == 1;
          const contentType = getPostMediaType(socialMediaPost);
          const platform = isSocialMedia ? getSocialMediaPlatform(socialMediaPost) : null;
          const postText = socialMediaPost.Post_Text.find(({ isSelected }) => isSelected);
          const {
            TotalImpact,
            ProductImpact,
            SocialPositioningImpact,
            IssuesImpact,
            IssuesSupportImpact,
          } = res?.social_post_data;

          const strategyData = {
            postTitle: Post_Title,
            brandName: Name,
            contentType: contentType,
            platform: platform,
            postText: postText.value,
            brief: Post_Client_Brief,
            audience: Post_Target_Audience,
            budget: Post_Budget,
            totalImpact: TotalImpact,
            productPositioningImpact: ProductImpact?.TotalProductImpact,
            socialPositioningImpact: SocialPositioningImpact?.TotalSocialPositioningImpact,
            socialIssuesImpact: IssuesImpact?.TotalIssuesImpact,
            causeSupportImpact: IssuesSupportImpact?.TotalIssuesSupportImpact,
          };
          AISocialMediaStrategyGenerator(strategyData, id, pickedPersona.id, marketingStrategies)
            .then(res => {
              dispatch(fetchSpecificSurveyData('social', appData.visibleAgencies));
              setAIStrategyGenerator({ loading: false, text: res });
            })
            .catch(err => {
              setAIStrategyGenerator({ loading: false, text: '' });
              showToastWarn();
            });
        }
      })
      .catch(err => {
        setCalculations({ results: null, loading: false });
        showToastWarn();
      });
  };

  const onFiltersSubmit = preparedFilterData => {
    setChosenFilters(preparedFilterData);
    calculateHandler(pickedSocialPost, pickedBrandPersona, preparedFilterData, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchQuarter = event => {
    const {
      target: { value },
    } = event;

    // On autofill we get a stringified value.
    const valueToSet = typeof value === 'string' ? value.split(',') : value;
    if (!valueToSet.length) return;

    const batchInfoToSet = { ...pickedBatchData, quarter: valueToSet };

    setPickedBatchData(batchInfoToSet);
  };

  const onConfirmBatchQuarterChange = () => {
    //Run calculation on quarter selector close
    //Run social algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(pickedSocialPost, pickedBrandPersona, chosenFilters, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchCountry = event => {
    const {
      target: { value },
    } = event;

    const newestBatchYear = Object.keys(appData.combinedBatchDataCodes[value])
      .sort()
      .pop();
    const quarters = appData.combinedBatchDataCodes[value][newestBatchYear].sort(batchDataSort);
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: newestBatchYear,
    };

    setPickedBatchData(batchInfoToSet);
    setPickedBatchCountry(value);

    //Run brand persona algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(pickedSocialPost, pickedBrandPersona, chosenFilters, batchInfoToSet, value);
  };

  const onChangeBatchYear = event => {
    const {
      target: { value },
    } = event;

    const quarters = appData.combinedBatchDataCodes[pickedBatchCountry][value].sort(batchDataSort);
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: value,
    };

    setPickedBatchData(batchInfoToSet);

    //Run social algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(pickedSocialPost, pickedBrandPersona, chosenFilters, batchInfoToSet, pickedBatchCountry);
  };

  const showToastWarn = () => {
    toast.warn('Something went wrong, please try again later.', {
      position: 'bottom-right',
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const appDataWithAllowedBrandPersonas = {
    ...appData,
    brandPersonas: appData.brandPersonas?.filter(e => pickedSocialPost?.agencyId === e.agencyId),
  };

  const appDataWithAllowedSocialPosts = {
    ...appData,
    socialPosts: appData.socialPosts?.filter(e => pickedSocialPost?.agencyId === e.agencyId),
  };

  return (
    <PageContainer>
      {pageIsLoading || calculations.loading ? <PageLoader /> : null}
      {calculations.results ? (
        <div ref={PDFContentRef}>
          <UserDropdownHeader />
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            padding="20px 20px 0px 20px"
            style={{ background: '#fff', gap: '10px 30px' }}>
            <Box flex="1">
              <HeaderSelector
                selectedId={id}
                data={appDataWithAllowedSocialPosts}
                type="social"
                setPickedId={setSocialPostPickedId}
                redirect={true}
              />
            </Box>

            <Box flexWrap="wrap" display="flex" justifyContent="flex-start" alignItems="center" style={{ gap: '0 20px' }}>
              <HeaderSelector
                selectedId={brandPersonaPickedId}
                data={appDataWithAllowedBrandPersonas}
                type="brand"
                setPickedId={setBrandPersonaPickedId}
              />
              <BuyerBatchSelector
                calculationType={'batch'}
                onChangeCalculationType={() => {}}
                pickedBuyerPersona={null}
                onChangeBuyerPersona={() => {}}
                pickedBatchData={pickedBatchData}
                pickedBatchCountry={pickedBatchCountry}
                onChangeBatchCountry={onChangeBatchCountry}
                onChangeBatchQuarter={onChangeBatchQuarter}
                onConfirmBatchQuarterChange={onConfirmBatchQuarterChange}
                onChangeBatchYear={onChangeBatchYear}
                type="social"
                withConsumerHeading
              />
            </Box>
          </Box>
          <Filters calculateHandler={onFiltersSubmit} themeColor="green" pickedBatchCountry={pickedBatchCountry} />
          <PDFExportButton
            type="green"
            dashboardType="marketing-strategy"
            dashboardId={id}
            exportFilters={chosenFilters}
            exportContext={{
              filters: chosenFilters,
              batchData: pickedBatchData,
              batchCountry: pickedBatchCountry,
              brandPersonaId: brandPersonaPickedId,
            }}
          />
          {calculations.results.no_data ? (
            <Box display="flex" justifyContent="center" alignItems="center" marginTop="100px">
              <Typography
                variant="h6"
                color="textSecondary"
                style={{
                  fontSize: '20px',
                  margin: '0',
                  padding: '0',
                  textAlign: 'center',
                  fontWeight: 'bold',
                }}>
                No data, choose other "Consumer Settings" combination.
              </Typography>
            </Box>
          ) : (
            <ChartCalculations
              type="social"
              data={calculations.results}
              persona={pickedBrandPersona}
              socialPost={pickedSocialPost}
              AIStrategyGenerator={AIStrategyGenerator}
              setAIStrategyGenerator={setAIStrategyGenerator}
              visibleAgencies={appData.visibleAgencies}
            />
          )}
        </div>
      ) : null}

      <ToastContainer
        theme="colored"
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </PageContainer>
  );
};

export default SocialMediaDashboard;
